import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  addReviewToProduct,
  getProductReview,
  getSingleProductData
} from '../Api/products.js';
import { sanitizeRichText, sanitizeUserText } from '../utils/sanitizeHtml.js';

import Box from '@mui/material/Box';
import Rating from '@mui/material/Rating';
import StarIcon from '@mui/icons-material/Star';
import RelatedProduct from '../components/RelatedProduct.jsx';
import Loader from '../components/Loader.jsx';
import { toast } from 'react-toastify';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

function SingleProduct({ isAuthenticated, onAddToCart }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [productCount, setProductCount] = useState(1);
  const [discount, setDiscount] = useState(0);
  const [rating, setRating] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [loader, setLoader] = useState(false);

  const [open, setOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewText, setReviewText] = useState('');

  const userData = JSON.parse(localStorage.getItem('user_data') || '{}');

  /* ======================
     EFFECTS
  ====================== */

  useEffect(() => {
    setLoader(true);
    getSingleProductData(id)
      .then((data) => setProduct(data))
      .catch(() => toast.error("Failed to load product"))
      .finally(() => setLoader(false));
  }, [id]);

  useEffect(() => {
    if (product?.sale_price && product?.regular_price) {
      const disc = 100 - (product.sale_price / product.regular_price) * 100;
      setDiscount(Math.round(disc));
    }
    setRating(product?.average_rating || 0);
    setRelatedProducts(product?.related_ids || []);
  }, [product]);

  useEffect(() => {
    getProductReview(id)
      .then((res) => {
        setAllReviews(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setAllReviews([]));
  }, [id]);

  /* ======================
     HANDLERS
  ====================== */

  const addCartItem = () => {
    if (!isAuthenticated) {
      toast.error("Please login first!");
      navigate('/login');
      return;
    }
    onAddToCart(product, productCount);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error("Please login first!");
      return;
    }
    if (!reviewRating || !reviewText.trim()) {
      toast.error("Rating & review required");
      return;
    }

    setLoader(true);
    try {
      await addReviewToProduct({
        product_id: id,
        review: reviewText,
        reviewer: userData.name || "Anonymous",
        reviewer_email: userData.email || "",
        rating: parseInt(reviewRating),
      });

      toast.success("Review submitted");
      const updatedReviews = await getProductReview(id);
      setAllReviews(Array.isArray(updatedReviews?.data) ? updatedReviews.data : []);
    } catch {
      toast.error("Failed to submit review");
    } finally {
      setLoader(false);
      setOpen(false);
      setReviewRating(0);
      setReviewText('');
    }
  };

  const slides = product?.images || [];

  /* ======================
     RENDER
  ====================== */

  if (loader) return <Loader />;

  return (
    <section className="sp-wrapper">
      <div className="sp-container">

        {/* PRODUCT TOP */}
        <div className="sp-content">

          {/* IMAGES */}
          <div className="sp-images">
            <div className="sp-carousel">
              <div className="sp-carousel-slide">
                {slides.length > 0 && (
                  <img src={slides[currentSlide]?.src} alt="Product" />
                )}
              </div>

              <div className="sp-dots">
                {slides.map((img, i) => (
                  <div
                    key={i}
                    className={`sp-dot ${currentSlide === i ? 'active' : ''}`}
                    onClick={() => setCurrentSlide(i)}
                  >
                    <img src={img.src} alt="thumb" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* DETAILS */}
          <div className="sp-details">
            <h2>{product?.name}</h2>

            {product?.sale_price ? (
              <>
                <div className="d-flex">
                  <strike>₹{product.regular_price}</strike>
                  <span style={{ color: "#77BA5D", marginLeft: 10 }}>
                    {discount}% off
                  </span>
                </div>
                <h3>₹{product.sale_price}</h3>
              </>
            ) : (
              <h3>₹{product?.regular_price}</h3>
            )}

            <p dangerouslySetInnerHTML={{ __html: sanitizeRichText(product?.description) }} />

            <div className="d-flex" style={{ gap: 20, marginTop: 20 }}>
              <div className="sp-qty">
                <button onClick={() => setProductCount(Math.max(1, productCount - 1))}>-</button>
                <h2>{productCount}</h2>
                <button onClick={() => setProductCount(productCount + 1)}>+</button>
              </div>

              <button className="sp-cart-btn" onClick={addCartItem}>
                Add to Cart
              </button>
            </div>
          </div>
        </div>

        {/* REVIEWS */}
        <div className="sp-review-box">
          <h2>Reviews</h2>
          <div className="sp-review-content">
            <Box>
              <Rating value={rating} readOnly precision={0.5}
                emptyIcon={<StarIcon style={{ opacity: 0.5 }} />} />
            </Box>

            <button className="sp-cart-btn" onClick={() => setOpen(true)}>
              Write Review
            </button>
          </div>
        </div>

        {/* ALL REVIEWS */}
        <div className="sp-show-reviews">
          <h2>Customer Reviews</h2>
          {Array.isArray(allReviews) && allReviews.length === 0 ? (
            <p className="no-reviews">No reviews yet</p>
          ) : (
            Array.isArray(allReviews) &&
            allReviews.map((r) => (
              <div className="sp-review-card" key={r.id}>
                <div className="sp-review-header">
                  <h4>{r.reviewer}</h4>
                  <Rating value={Number(r.rating)} readOnly size="small" />
                </div>

                <div
                  className="sp-review-content-section"
                  dangerouslySetInnerHTML={{ __html: sanitizeUserText(r.review) }}
                />
              </div>
            ))
          )}
        </div>

        {/* RELATED PRODUCTS */}
        <div className="sp-related">
          <h2>Related Products</h2>
          <div className="sp-related-list">
            {relatedProducts.map((pid) => (
              <RelatedProduct
                key={pid}
                productId={pid}
                isAuthenticated={isAuthenticated}
                onAddToCart={onAddToCart}
              />
            ))}
          </div>
        </div>

      </div>

      {/* REVIEW MODAL */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6">Write Review</Typography>
          <form onSubmit={handleReviewSubmit}>
            <Rating
              value={reviewRating}
              onChange={(e, val) => setReviewRating(val)}
              precision={0.5}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              sx={{ mt: 2 }}
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
            />
            <Button type="submit" variant="contained" sx={{ mt: 2 }}>
              Submit
            </Button>
          </form>
        </Box>
      </Modal>
    </section>
  );
}

export default SingleProduct;
